import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  deleteNotification as deleteNotificationRequest,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  Notification,
  NotificationsResponse,
} from '../lib/notificationApi';

const POLL_INTERVAL_MS = 30_000;
const PAGE_SIZE = 30;

/** The newest pages, until at least `want` rows are in hand or none are left. */
async function loadNewest(want: number) {
  const rows: Notification[] = [];
  let cursor: string | null = null;
  let visibleFor: number | null = null;
  do {
    const page: NotificationsResponse = await fetchNotifications({ limit: PAGE_SIZE, before: cursor });
    rows.push(...page.notifications);
    cursor = page.nextCursor ?? null;
    visibleFor = page.readVisibleForMs ?? null;
  } while (cursor && rows.length < want);
  return { rows, cursor, visibleFor };
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  /** True while there are older notifications not loaded yet. */
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Removes it from this user's bell (the server keeps it for everyone else). */
  deleteNotification: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
};

/**
 * The same context, or null outside the provider. For a page-level banner that
 * should render nothing - not throw - where no provider is mounted (page tests).
 */
export const useOptionalNotifications = (): NotificationContextType | null =>
  useContext(NotificationContext) ?? null;

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [readVisibleForMs, setReadVisibleForMs] = useState<number | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // How many rows are on screen, so a poll re-reads as many as the user has
  // scrolled through rather than snapping the list back to the first page.
  const loadedCount = useRef(0);
  // Bumped by every refresh: a "load more" page that lands after a refresh
  // belongs to the list the refresh replaced, so it is dropped.
  const generation = useRef(0);

  useEffect(() => {
    loadedCount.current = notifications.length;
  }, [notifications]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    const gen = ++generation.current;
    try {
      const want = Math.max(PAGE_SIZE, loadedCount.current);
      const [{ rows, cursor, visibleFor }, count] = await Promise.all([
        loadNewest(want),
        fetchUnreadCount(),
      ]);
      if (gen !== generation.current) return;
      setNotifications(rows);
      setNextCursor(cursor);
      setReadVisibleForMs(visibleFor);
      setUnreadCount(count);
    } catch {
      // silently ignore — network errors shouldn't surface in UI
    }
  }, [isAuthenticated]);

  const initialLoad = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    await refresh();
    setIsLoading(false);
  }, [isAuthenticated, refresh]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setNextCursor(null);
      if (pollTimer.current) clearInterval(pollTimer.current);
      return;
    }

    initialLoad();

    pollTimer.current = setInterval(refresh, POLL_INTERVAL_MS);

    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [isAuthenticated, initialLoad, refresh]);

  // A read notification leaves the bell readVisibleForMs after it was read.
  // The server decides; this only asks it again as the next one falls due,
  // instead of waiting for the next poll. Only times still ahead count: a row
  // the server kept past its time here (this clock ahead of the server's) is
  // left to the poll, so it can never re-arm this every second. A time beyond
  // the next poll is left to the poll too: a browser timer holds at most ~24.8
  // days and fires AT ONCE past that, so a 30-day wait would loop.
  useEffect(() => {
    if (readVisibleForMs === null) return;
    const now = Date.now();
    const due = notifications
      .filter((n) => n.isRead && n.readAt)
      .map((n) => new Date(n.readAt as string).getTime() + readVisibleForMs)
      .filter((t) => t > now);
    if (due.length === 0) return;
    const wait = Math.min(...due) - now + 1000;
    if (wait > POLL_INTERVAL_MS) return;
    const timer = setTimeout(() => void refresh(), wait);
    return () => clearTimeout(timer);
  }, [notifications, readVisibleForMs, refresh]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;
    const gen = generation.current;
    setIsLoadingMore(true);
    try {
      const page = await fetchNotifications({ limit: PAGE_SIZE, before: nextCursor });
      if (gen !== generation.current) return;
      setNotifications((prev) => {
        const have = new Set(prev.map((n) => n._id));
        return [...prev, ...page.notifications.filter((n) => !have.has(n._id))];
      });
      setNextCursor(page.nextCursor ?? null);
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextCursor, isLoadingMore]);

  const markAsRead = useCallback(
    async (id: string) => {
      await markNotificationAsRead(id);
      const readAt = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true, readAt: n.readAt ?? readAt } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    },
    []
  );

  const markAllRead = useCallback(async () => {
    await markAllNotificationsAsRead();
    const readAt = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt }))
    );
    setUnreadCount(0);
  }, []);

  const deleteNotification = useCallback(
    async (id: string) => {
      await deleteNotificationRequest(id);
      const wasUnread = notifications.some((n) => n._id === id && !n.isRead);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));
    },
    [notifications]
  );

  // This provider wraps the entire authenticated app (App.tsx) and polls
  // every POLL_INTERVAL_MS — an unmemoized value object here re-renders every
  // mounted consumer on every poll tick regardless of whether the data
  // actually changed. Mirrors AuthContext.tsx's existing value memoization.
  const value = useMemo<NotificationContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      hasMore: nextCursor !== null,
      isLoadingMore,
      loadMore,
      markAsRead,
      markAllRead,
      deleteNotification,
      refresh,
    }),
    [notifications, unreadCount, isLoading, nextCursor, isLoadingMore, loadMore, markAsRead, markAllRead, deleteNotification, refresh]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
